import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePillar } from '../contexts/PillarContext';
import { logout as logoutService } from '../services/authService';

/** Map pillar to home route */
function pillarHome(pillar: string) {
  if (pillar === 'rate') return '/rate';
  if (pillar === 'newbuild') return '/newbuild';
  if (pillar === 'lab') return '/';
  return '/search';
}

/** Map pillar to logo fill */
function pillarLogoFill(pillar: string) {
  if (pillar === 'rate') return 'rgba(0, 206, 209, 0.2)';
  if (pillar === 'newbuild') return 'rgba(199, 91, 74, 0.2)';
  if (pillar === 'lab') return 'rgba(55, 65, 81, 0.2)';
  return 'rgba(99, 102, 241, 0.2)';
}

/** Map pillar to logo stroke */
function pillarLogoStroke(pillar: string) {
  if (pillar === 'rate') return 'rgba(0, 206, 209, 0.9)';
  if (pillar === 'newbuild') return 'rgba(199, 91, 74, 0.9)';
  if (pillar === 'lab') return 'rgba(156, 163, 175, 0.9)';
  return 'rgba(165, 180, 252, 0.9)';
}

/** Map pillar to accent label */
function pillarLabel(pillar: string) {
  if (pillar === 'rate') return 'Rate';
  if (pillar === 'newbuild') return 'Newbuild';
  if (pillar === 'lab') return 'Lab';
  return 'Search';
}

/** Pillar-specific profile dropdown items (shared between desktop and mobile) */
function ProfileMenuItems({ pillar, onNavigate }: { pillar: string; onNavigate?: () => void }) {
  const items = pillar === 'lab' ? (
    <>
      <Link to="/lab/profile" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span> Mein SurrealEstateLab-Profil
      </Link>
      <Link to="/lab/profile#password" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span> Passwort ändern
      </Link>
    </>
  ) : pillar === 'newbuild' ? (
    <>
      <Link to="/newbuild/profile" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span> Mein Profil
      </Link>
      <Link to="/newbuild/profile?tab=projects" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></span> Meine Neubauprojekte
      </Link>
      <Link to="/newbuild/profile?tab=newbuild-edit" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></span> Mein Neubauprofil bearbeiten
      </Link>
      <Link to="/lab/profile#password" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span> Passwort ändern
      </Link>
    </>
  ) : pillar === 'rate' ? (
    <>
      <Link to="/rate/profile" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span> Mein Profil
      </Link>
      <Link to="/rate/profile?tab=valuations" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg></span> Meine Analysen
      </Link>
      <Link to="/lab/profile#password" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span> Passwort ändern
      </Link>
    </>
  ) : (
    <>
      <Link to="/profile" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg></span> Mein Profil
      </Link>
      <Link to="/profile?tab=searches" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg></span> Meine Suchen
      </Link>
      <Link to="/profile?tab=favorites" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/></svg></span> Favoriten
      </Link>
      <Link to="/profile?tab=comparisons" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="3" y1="20" x2="21" y2="20"/></svg></span> Objekte vergleichen
      </Link>
      <Link to="/profile?tab=lifestyle-edit" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></span> Lebensstil bearbeiten
      </Link>
      <Link to="/profile?tab=my-places" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg></span> Meine besonderen Orte
      </Link>
      <Link to="/lab/profile#password" className="nav-dropdown-item" onClick={onNavigate}>
        <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg></span> Passwort ändern
      </Link>
    </>
  );

  return <>{items}</>;
}

export default function Header() {
  const { user, isAuthenticated, logout } = useAuth();
  const { pillar } = usePillar();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const burgerRef = useRef<HTMLButtonElement>(null);

  /* Close mobile menu on route change */
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname, location.search, location.hash]);

  /* Close mobile menu on click outside (exclude burger to avoid toggle race) */
  useEffect(() => {
    if (!menuOpen) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (
        drawerRef.current && !drawerRef.current.contains(target) &&
        burgerRef.current && !burgerRef.current.contains(target)
      ) {
        setMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [menuOpen]);

  /* Lock body scroll when mobile menu open */
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const handleLogout = async () => {
    setMenuOpen(false);
    await logoutService();
    logout();
    navigate('/');
  };

  const closeMobile = () => setMenuOpen(false);

  return (
    <>
      <header className={`header${menuOpen ? ' header--menu-open' : ''}`}>
        <div className="header-inner">
          {/* ── Logo ── */}
          <Link to={pillarHome(pillar)} className="logo">
            <span className="logo-icon"><svg viewBox="0 0 24 26" width="22" height="24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z" fill={pillarLogoFill(pillar)} stroke={pillarLogoStroke(pillar)}/></svg></span>
            <span className="logo-text">SurrealEstate<span className={`logo-accent ${pillar === 'newbuild' ? 'logo-accent--newbuild' : ''}`}>{pillarLabel(pillar)}</span></span>
          </Link>

          {/* ── Desktop navigation (hidden on mobile) ── */}
          <nav className="nav nav--desktop">
            <Link to={pillarHome(pillar)} className="nav-link">Home</Link>
            <Link to="/" className={`nav-badge nav-badge--lab ${pillar === 'lab' ? 'nav-badge--active' : ''}`}>SurrealEstate<span className="nav-badge-accent nav-badge-accent--lab">Lab</span></Link>
            <Link to="/search" className={`nav-badge nav-badge--search ${pillar === 'search' ? 'nav-badge--active' : ''}`}>SurrealEstate<span className="nav-badge-accent nav-badge-accent--search">Search</span></Link>
            <Link to="/rate" className={`nav-badge nav-badge--rate ${pillar === 'rate' ? 'nav-badge--active' : ''}`}>SurrealEstate<span className="nav-badge-accent nav-badge-accent--rate">Rate</span></Link>
            <Link to="/newbuild" className={`nav-badge nav-badge--newbuild ${pillar === 'newbuild' ? 'nav-badge--active' : ''}`}>SurrealEstate<span className="nav-badge-accent nav-badge-accent--newbuild">Newbuild</span></Link>
            
            <div className="nav-divider"></div>
            
            {isAuthenticated && user ? (
              <div className="nav-user-dropdown">
                <span className="nav-user"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg> {user.name}</span>
                <div className="nav-dropdown-menu">
                  <ProfileMenuItems pillar={pillar} />
                  <div className="nav-dropdown-divider"></div>
                  <button onClick={handleLogout} className="nav-dropdown-item nav-dropdown-item--logout">
                    <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span> Abmelden
                  </button>
                </div>
              </div>
            ) : (
              <>
                <Link to="/#lab-auth" className="nav-link">Anmelden</Link>
                <Link to="/#lab-auth" className="nav-link nav-link--primary">Registrieren</Link>
              </>
            )}
          </nav>

          {/* ── Hamburger button (visible only on mobile) ── */}
          <button
            ref={burgerRef}
            className={`burger ${menuOpen ? 'burger--open' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menü"
            aria-expanded={menuOpen}
          >
            <span className="burger-line" />
            <span className="burger-line" />
            <span className="burger-line" />
          </button>
        </div>
      </header>

      {/* ── Mobile overlay (outside header to avoid stacking context issues) ── */}
      <div
        className={`mobile-overlay ${menuOpen ? 'mobile-overlay--visible' : ''}`}
        onClick={closeMobile}
      />

      {/* ── Mobile drawer (outside header – header stays on top, burger X always tappable) ── */}
      <div ref={drawerRef} className={`mobile-drawer ${menuOpen ? 'mobile-drawer--open' : ''}`}>
        {/* ── Pillar navigation ── */}
        <div className="mobile-drawer-section">
          <div className="mobile-drawer-label">Navigation</div>
          <Link to={pillarHome(pillar)} className="mobile-drawer-link" onClick={closeMobile}>
            <svg viewBox="0 0 24 26" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1L1 11v14h6v-8h10v8h6V11L12 1z"/></svg>
            Home
          </Link>
          <Link to="/" className={`mobile-drawer-badge mobile-drawer-badge--lab ${pillar === 'lab' ? 'mobile-drawer-badge--active' : ''}`} onClick={closeMobile}>
            SurrealEstate<span className="nav-badge-accent nav-badge-accent--lab">Lab</span>
          </Link>
          <Link to="/search" className={`mobile-drawer-badge mobile-drawer-badge--search ${pillar === 'search' ? 'mobile-drawer-badge--active' : ''}`} onClick={closeMobile}>
            SurrealEstate<span className="nav-badge-accent nav-badge-accent--search">Search</span>
          </Link>
          <Link to="/rate" className={`mobile-drawer-badge mobile-drawer-badge--rate ${pillar === 'rate' ? 'mobile-drawer-badge--active' : ''}`} onClick={closeMobile}>
            SurrealEstate<span className="nav-badge-accent nav-badge-accent--rate">Rate</span>
          </Link>
          <Link to="/newbuild" className={`mobile-drawer-badge mobile-drawer-badge--newbuild ${pillar === 'newbuild' ? 'mobile-drawer-badge--active' : ''}`} onClick={closeMobile}>
            SurrealEstate<span className="nav-badge-accent nav-badge-accent--newbuild">Newbuild</span>
          </Link>
        </div>

        {/* ── Profile / Auth section ── */}
        <div className="mobile-drawer-divider" />
        <div className="mobile-drawer-section">
          {isAuthenticated && user ? (
            <>
              <div className="mobile-drawer-label">
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                {user.name}
              </div>
              <ProfileMenuItems pillar={pillar} onNavigate={closeMobile} />
              <div className="mobile-drawer-divider" />
              <button onClick={handleLogout} className="nav-dropdown-item nav-dropdown-item--logout">
                <span className="nav-dropdown-icon"><svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span> Abmelden
              </button>
            </>
          ) : (
            <>
              <div className="mobile-drawer-label">Konto</div>
              <Link to="/#lab-auth" className="mobile-drawer-link" onClick={closeMobile}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" y1="12" x2="3" y2="12"/></svg>
                Anmelden
              </Link>
              <Link to="/#lab-auth" className="mobile-drawer-link mobile-drawer-link--primary" onClick={closeMobile}>
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="8.5" cy="7" r="4"/><line x1="20" y1="8" x2="20" y2="14"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                Registrieren
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}
